import { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import {
  TInsuranceDuplicatedMember,
  TInsurancePerson,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { INTL_MONTH_DAY_FORMAT, intlFormat } from '@/common/utils/date';
import { InsuranceDuplicatedFamilyMembersError } from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/InsurancePlanChooseStep/components/InsuranceUpgradePlanConfirmationModal/components/InsuranceDuplicatedFamilyMembersError/InsuranceDuplicatedFamilyMembersError';
import { InsuranceDuplicatedFamilyMembersWarning } from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/InsurancePlanChooseStep/components/InsuranceUpgradePlanConfirmationModal/components/InsuranceDuplicatedFamilyMembersWarning/InsuranceDuplicatedFamilyMembersWarning';
import { determineInsuranceUpgradeFlags } from '@/web/containers/Insurance/InsuranceUpgrade/common/utils';
import { type IButtonProps } from '@coverflex-tech/hypernova/';

export const useInsuranceUpgradePlanConfirmationModalContent: TUseInsuranceUpgradeConfirmationModal =
  ({
    persons = [],
    displayFamilyMembersUI = false,
    enrolEndDate,
    allDuplicatedFamilyMembers,
    onAddFamilyMembers,
    onClose,
    onNext,
    onSubmit,
  }) => {
    const formattedEnrolEndDate = intlFormat(
      enrolEndDate,
      INTL_MONTH_DAY_FORMAT
    );

    return (() => {
      const {
        employeePaysZero,
        familyMembersAdded,
        familyMembersIncluded,
        selectedDuplicatedMembers,
        selectedAlreadyInsuredDuplicatedMembers,
        hasDuplicatedMembersSelected,
        hasAlreadyInsuredDuplicatedMembersSelected,
      } = determineInsuranceUpgradeFlags(
        {
          persons,
        },
        allDuplicatedFamilyMembers
      );

      const flags = {
        employeePaysZero,
        familyMembersAdded,
        familyMembersIncluded,
        displayFamilyMembersUI,
        selectedDuplicatedMembers,
        selectedAlreadyInsuredDuplicatedMembers,
        hasDuplicatedMembersSelected,
        hasAlreadyInsuredDuplicatedMembersSelected,
      };

      if (employeePaysZero) {
        if (!displayFamilyMembersUI) {
          return {
            ...flags,
            content: (
              <Trans
                i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.description.nothing_to_pay.no_family_allowed"
                values={{ date: formattedEnrolEndDate }}
              />
            ),
            primaryAction: {
              children: (
                <Trans i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.confirm_label" />
              ),
              onClick: () => onSubmit?.(),
            },
            secondaryAction: {
              children: <Trans i18nKey="general.navigation.cancel" />,
              onClick: () => onClose?.(),
            },
          };
        }

        if (!familyMembersAdded) {
          return {
            ...flags,
            content: (
              <Trans
                i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.description.nothing_to_pay.family_allowed.no_added_family"
                values={{ date: formattedEnrolEndDate }}
              />
            ),
            primaryAction: {
              children: (
                <Trans i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.confirm_label" />
              ),
              onClick: () => onSubmit?.(),
            },
            secondaryAction: {
              children: (
                <Trans i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.add_family_members_label" />
              ),
              onClick: () => onAddFamilyMembers?.(),
            },
          };
        }

        if (hasDuplicatedMembersSelected) {
          if (hasAlreadyInsuredDuplicatedMembersSelected) {
            return {
              ...flags,
              content: (
                <InsuranceDuplicatedFamilyMembersError
                  duplicatedFamilyMembers={
                    selectedAlreadyInsuredDuplicatedMembers
                  }
                />
              ),
              primaryAction: {
                children: <Trans i18nKey="general.navigation.close" />,
                onClick: () => onClose?.(),
              },
              secondaryAction: undefined,
            };
          }

          return {
            ...flags,
            content: (
              <>
                <InsuranceDuplicatedFamilyMembersWarning
                  duplicatedFamilyMembers={selectedDuplicatedMembers}
                />
                <br />
                <Trans
                  i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.description.nothing_to_pay.family_allowed.added_family.included_family"
                  values={{ date: formattedEnrolEndDate }}
                />
              </>
            ),
            primaryAction: {
              children: (
                <Trans i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.confirm_label" />
              ),
              onClick: () => onSubmit?.(),
            },
            secondaryAction: {
              children: <Trans i18nKey="general.navigation.cancel" />,
              onClick: () => onClose?.(),
            },
          };
        }

        return {
          ...flags,
          content: (
            <Trans
              i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.description.nothing_to_pay.family_allowed.added_family.included_family"
              values={{ date: formattedEnrolEndDate }}
            />
          ),
          primaryAction: {
            children: (
              <Trans i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.confirm_label" />
            ),
            onClick: () => onSubmit?.(),
          },
          secondaryAction: {
            children: <Trans i18nKey="general.navigation.cancel" />,
            onClick: () => onClose?.(),
          },
        };
      }

      if (displayFamilyMembersUI) {
        if (!familyMembersAdded) {
          return {
            ...flags,
            content: (
              <Trans i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.description.have_to_pay.family_allowed.no_added_family" />
            ),
            primaryAction: {
              children: <Trans i18nKey="general.navigation.next" />,
              onClick: () => onNext?.(),
            },
            secondaryAction: {
              children: (
                <Trans i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.add_family_members_label" />
              ),
              onClick: () => onAddFamilyMembers?.(),
            },
          };
        }

        if (hasDuplicatedMembersSelected) {
          if (hasAlreadyInsuredDuplicatedMembersSelected) {
            return {
              ...flags,
              content: (
                <InsuranceDuplicatedFamilyMembersError
                  duplicatedFamilyMembers={
                    selectedAlreadyInsuredDuplicatedMembers
                  }
                />
              ),
              primaryAction: {
                children: <Trans i18nKey="general.navigation.close" />,
                onClick: () => onClose?.(),
              },
              secondaryAction: undefined,
            };
          }

          return {
            ...flags,
            content: (
              <InsuranceDuplicatedFamilyMembersWarning
                duplicatedFamilyMembers={selectedDuplicatedMembers}
              />
            ),
            primaryAction: {
              children: <Trans i18nKey="general.navigation.next" />,
              onClick: () => onNext?.(),
            },
            secondaryAction: {
              children: <Trans i18nKey="general.navigation.cancel" />,
              onClick: () => onClose?.(),
            },
          };
        }
      }

      return {
        ...flags,
        content: (
          <Trans
            i18nKey="dashboard.insurance.upgrade_plan_confirmation_modal.description.nothing_to_pay.family_allowed.added_family.included_family"
            values={{ date: formattedEnrolEndDate }}
          />
        ),
        primaryAction: {
          children: <Trans i18nKey="general.navigation.next" />,
          onClick: () => onNext?.(),
        },
        secondaryAction: {
          children: <Trans i18nKey="general.navigation.cancel" />,
          onClick: () => onClose?.(),
        },
      };
    })();
  };

export type TUseInsuranceUpgradeConfirmationModal = (args: {
  persons?: TInsurancePerson[];
  displayFamilyMembersUI?: boolean;
  enrolEndDate?: string;
  allDuplicatedFamilyMembers: TInsuranceDuplicatedMember[];
  onClose?: Function;
  onSubmit?: Function;
  onNext?: Function;
  onAddFamilyMembers?: Function;
}) => {
  content: ReactNode;
  employeePaysZero: boolean;
  displayFamilyMembersUI: boolean;
  familyMembersAdded: boolean;
  familyMembersIncluded: boolean;
  selectedDuplicatedMembers: TInsuranceDuplicatedMember[];
  hasDuplicatedMembersSelected: boolean;
  hasAlreadyInsuredDuplicatedMembersSelected: boolean;
  primaryAction: IButtonProps;
  secondaryAction?: IButtonProps;
};
